import React, { useEffect, useState } from "react";
import { Accordion, Col, Form, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { contactUsSchema } from "../../utils/validationSchemas";
import { successMsg } from "../../utils/toastMessage";
import { contactUsReason } from "../../utils/constants";
import { addQuery } from "../../redux/queries/thunk";
import { handleApiRequest } from "../../services/handleApiRequest";
import { ErrorField } from "../../Component/common/errorField";
import MySelect from "../../Component/common/mySelect";
import AskToShare from "../../Component/askToShare";
import HeartLine from "../../Component/common/heartLine";
import {
  FacebookHandle,
  InstagramHandle,
  TwitterHandle,
  YoutubeHandle,
} from "../../Component/common/socialHandles";
import { contactUsRoute, faqs } from "../../routes/routesPath";
import MyHelmet from "../../seo/helmet";
import { PhoneInput } from "../../Component/formComponent/components/inputFields";

const defaultValues = {
  name: "",
  mobile: "",
  email: "",
  reason: "",
  comment: "",
};

const ContactUs = () => {
  const { pathname } = useLocation();

  return (
    <>
      {pathname.includes(contactUsRoute) && (
        <MyHelmet
          title={"Contact us to share your feedback"}
          keywords={[]}
          description="FeelingHub is always there to help and listen to you. Contact us to share your feedback, suggestion or complaints."
          link={contactUsRoute}
        />
      )}

      <section className="contactusWrapper position-relative">
        <ContactUsForm />
        <HeartLine />
        <Faqs />
        <HeartLine />
        <AskToShare />
      </section>
    </>
  );
};

export const ContactUsForm = () => {
  const { pathname } = useLocation();
  const { userProfile = {} } = useSelector((state) => state.auth);

  const handleSubmitQuery = async () => {
    const response = await handleApiRequest(addQuery, values);
    resetForm();

    if (response.status) {
      successMsg("Thanks for your response");
    }
  };

  let {
    values,
    errors,
    handleChange,
    setFieldValue,
    setValues,
    handleSubmit,
    isSubmitting,
    resetForm,
    touched,
  } = useFormik({
    initialValues: defaultValues,
    validationSchema: contactUsSchema,
    onSubmit: handleSubmitQuery,
  });

  useEffect(() => {
    if (userProfile._id) {
      const { name, mobile, email } = userProfile;
      setValues({ ...defaultValues, name, mobile, email });
    }
  }, [userProfile]);

  return (
    <Row className="align-items-center m-0 px-lg-5 px-2 my-5">
      <Col md={5}>
        <button className="outlineBtn rounded-pill mb-3">Don't be shy</button>
        {pathname.includes(contactUsRoute) ? (
          <h1 className="fw-normal">
            Get in <span className="text-primary">Touch</span>
          </h1>
        ) : (
          <h2 className="fw-normal">
            Get in <span className="text-primary">Touch</span>
          </h2>
        )}
        <p>
          If you are interested in hearing more or express yourself in different ways, thinking of
          working with us, or just think it would be good to have a chat? Just tell us a bit about
          yourself and we’ll be in touch.
        </p>
        <div className="d-none d-md-block">
          <div className="mt-2 d-flex align-items-center">
            <FacebookHandle />
            <InstagramHandle />
            <YoutubeHandle />
            <TwitterHandle />
          </div>
        </div>
      </Col>
      <Col md={1} className="d-none d-md-block" />
      <Col md={6}>
        <h2 className="fw-normal">
          Let's <span className="text-primary">Talk</span>
        </h2>
        <p className="">Let’s bring your idea to life together.</p>
        <Form onSubmit={handleSubmit} className="max-w-500">
          <div className="mt-3">
            <input
              type="text"
              className="form-control"
              placeholder="Enter Full Name*"
              name="name"
              value={values.name}
              onChange={handleChange}
            />
            {touched.name && <ErrorField touched={touched} error={errors.name} />}
          </div>

          <Row>
            <Col sm={6} className="mt-3 pe-sm-0">
              <div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Email Address*"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                />
                {touched.email && <ErrorField touched={touched} error={errors.email} />}
              </div>
            </Col>
            <Col sm={6} className="mt-3">
              <div>
                <PhoneInput
                  placeholder="Phone Phone*"
                  name="mobile"
                  value={values.mobile}
                  onChange={(value, data, e, formattedValue) => {
                    setFieldValue("mobile", formattedValue);
                  }}
                />

                {touched.mobile && <ErrorField touched={touched} error={errors.mobile} />}
              </div>
            </Col>
          </Row>

          <div className="mt-3">
            <MySelect
              placeholder="Select reason*"
              options={contactUsReason}
              value={values.reason}
              onChange={(selected) => setFieldValue("reason", selected)}
            />

            {touched.reason && <ErrorField touched={touched} error={errors.reason} />}
          </div>

          <div className="my-3 my-sm-3">
            <textarea
              className="form-control"
              placeholder="Your message*"
              name="comment"
              value={values.comment}
              onChange={handleChange}
            />
            {touched.comment && <ErrorField touched={touched} error={errors.comment} />}
          </div>

          {isSubmitting ? (
            <button className="secondaryBtn muted rounded-pill px-3 py-1 text-white" disabled>
              Submitting
              <i className="growLoader spinner-grow ms-2" role="status" />
            </button>
          ) : (
            <button
              type="submit"
              className="primaryBtn rounded-pill px-3 py-1"
              disabled={isSubmitting}>
              Send
            </button>
          )}
        </Form>
      </Col>
    </Row>
  );
};

export const Faqs = ({ className = "", initialData = {} }) => {
  const { pathname } = useLocation();
  const { faqsList: globalFaqsList = [] } = useSelector((state) => state.common || {});

  const [faqsList, setFaqList] = useState(initialData.faqs || globalFaqsList);

  useEffect(() => {
    if (!initialData.faqs && globalFaqsList) {
      setFaqList(globalFaqsList);
    }
  }, [initialData, globalFaqsList]);

  return (
    <div className={`mx-2 mx-lg-5 px-lg-5 my-5 ${className}`}>
      {pathname.includes(faqs) ? (
        <h1>Frequently Asked Questions (FAQS)</h1>
      ) : (
        <h2>Frequently Asked Questions (FAQS)</h2>
      )}
      <Accordion defaultActiveKey={faqsList?.[0]?._id} className="faqsAccordion">
        {faqsList.map((faq) => (
          <Accordion.Item
            key={faq._id}
            eventKey={faq._id}
            className="my-3 border shadow-sm rounded-0 rounded-top">
            <Accordion.Header>{faq.question}</Accordion.Header>
            <Accordion.Body>
              <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

export default ContactUs;
